/**
 * Validate HTTP header field names according to RFC 7230 specifications.
 * @param val The header field name to validate.
 * @returns True if the header field name is valid, false otherwise.
 *
 * @description HTTP header field names must follow RFC 7230 specifications
 *  - Must not be empty
 *  - Can contain: letters, digits, hyphens
 *  - Must start with a letter or digit
 *  - Cannot contain spaces, underscores, or special characters except hyphens
 *  - Common headers like Content-Type, Authorization, X-Custom-Header are valid
 */
export function validateHeaderKey(val: string): boolean {
  if (!val || val.length === 0) return false;
  // RFC 7230: field-name = token
  // token = 1*tchar
  // tchar = "!" / "#" / "$" / "%" / "&" / "'" / "*" / "+" / "-" / "." /
  //         "^" / "_" / "`" / "|" / "~" / DIGIT / ALPHA
  const httpHeaderRegex = /^[a-zA-Z0-9!#$%&'*+\-.^_`|~]+$/;
  return httpHeaderRegex.test(val);
}

/**
 * Validate a list of HTTP headers expressed as "Key=Value" strings.
 * @param headers The headers to validate.
 * @returns True if every header is a valid "Key=Value" pair, false otherwise.
 */
export function validateHeaders(headers: string[] | null | undefined): boolean {
  if (!headers) return false;
  return headers.every((header) => {
    const val = header.trim().replace(/\s+/g, "");
    if (val === "") return false;
    const match = val.match(/^([^=:]+)=([^=:]+)$/);
    if (!match) return false;
    const [, key, value] = match.map((s) => s.trim());
    if (key === "" || value === "") return false;
    if (!validateHeaderKey(key)) return false;
    return true;
  });
}

/**
 * Parse a list of "Key=Value" header strings into a headers object.
 * @param headers The headers expressed as "Key=Value" strings.
 * @returns A record of header name to value. Invalid entries are skipped silently.
 */
export function parseHeaders(headers: string[] | null | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!headers) return result;
  for (const header of headers) {
    const val = header.trim().replace(/\s+/g, "");
    const match = val.match(/^([^=:]+)=([^=:]+)$/);
    if (!match) continue;
    const [, key, value] = match;
    result[key.trim()] = value.trim();
  }
  return result;
}
